import React, { useCallback, useId, useRef, useState, useEffect } from 'react';
import { Upload, Globe, Lock, Plus, Trash2, BookOpen, Info, MessageSquare, Bold, Italic } from 'lucide-react';

const MAX_MEDIA_SIZE_BYTES = 50 * 1024 * 1024;
const MIN_SELECTION_SIZE = 0.05;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const cropImageToBlob = (image, selection, mimeType) => {
  const cropX = Math.round(selection.x * image.width);
  const cropY = Math.round(selection.y * image.height);
  const cropWidth = Math.max(1, Math.round(selection.width * image.width));
  const cropHeight = Math.max(1, Math.round(selection.height * image.height));
  const canvas = document.createElement('canvas');
  canvas.width = cropWidth;
  canvas.height = cropHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to compose cropped image.'));
        return;
      }
      resolve(blob);
    }, mimeType || 'image/jpeg');
  });
};

const canvasToBlob = (canvas, mimeType = 'image/png') =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to create message image.'));
        return;
      }
      resolve(blob);
    }, mimeType);
  });

const wrapCanvasText = (ctx, text, maxWidth) => {
  const paragraphs = String(text || '').split('\n');
  const lines = [];

  paragraphs.forEach((paragraph) => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      return;
    }

    let line = '';
    words.forEach((word) => {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    });
    lines.push(line);
  });

  return lines;
};

const messageCardBackgrounds = [
  '#fff7ed',
  '#fff1f8',
  '#f8fafc',
  '#fef3c7',
  '#ecfdf5',
  '#f5f3ff',
  '#4a1208',
  '#102a43',
];

export const MediaSection = ({ 
  mediaFiles, 
  isMediaLoading = false,
  mediaError = '',
  newMediaName, 
  setNewMediaName, 
  newMediaFile, 
  setNewMediaFile, 
  isMediaPublic, 
  setIsMediaPublic, 
  onUpload, 
  onToggleMediaAccess,
  onDeleteMedia,
  onAddTagAccess 
}) => {
  const mediaUploadInputId = useId();
  const previewUrlRef = useRef('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loadedImage, setLoadedImage] = useState(null);
  const [selection, setSelection] = useState({ x: 0, y: 0, width: 1, height: 1 });
  const [editorAspectRatio, setEditorAspectRatio] = useState(16 / 9);
  const [isCropBusy, setIsCropBusy] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageBackground, setMessageBackground] = useState('#fff7ed');
  const [messageTextColor, setMessageTextColor] = useState('#2b1d27');
  const [messageAlign, setMessageAlign] = useState('center');
  const [messageFontSize, setMessageFontSize] = useState(54);
  const [messageBold, setMessageBold] = useState(true);
  const [messageItalic, setMessageItalic] = useState(false);
  const [isMessageBusy, setIsMessageBusy] = useState(false);
  const [accessUpdateById, setAccessUpdateById] = useState({});
  const [accessErrorById, setAccessErrorById] = useState({});
  const [deleteUpdateById, setDeleteUpdateById] = useState({});
  const [deleteErrorById, setDeleteErrorById] = useState({});
  const editAreaRef = useRef(null);
  const editCanvasRef = useRef(null);
  const dragStateRef = useRef(null);

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
  };

  const updatePreviewUrl = (url) => {
    revokePreviewUrl();
    previewUrlRef.current = url;
    setPreviewUrl(url);
  };

  useEffect(() => () => revokePreviewUrl(), []);

  useEffect(() => {
    if (!loadedImage) {
      setEditorAspectRatio(16 / 9);
      setSelection({ x: 0, y: 0, width: 1, height: 1 });
      return;
    }
    setEditorAspectRatio(Math.max(0.1, loadedImage.width / Math.max(loadedImage.height, 1)));
    setSelection({ x: 0, y: 0, width: 1, height: 1 });
  }, [loadedImage]);

  const getDisplayMetrics = useCallback(() => {
    if (!loadedImage || !editAreaRef.current) return null;
    const rect = editAreaRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scale = Math.min(rect.width / loadedImage.width, rect.height / loadedImage.height);
    const displayWidth = loadedImage.width * scale;
    const displayHeight = loadedImage.height * scale;
    const offsetX = (rect.width - displayWidth) / 2;
    const offsetY = (rect.height - displayHeight) / 2;
    return { rect, displayWidth, displayHeight, offsetX, offsetY };
  }, [loadedImage]);

  const pointToImageCoords = (clientX, clientY, metrics) => {
    if (!metrics) return null;
    const relativeX = (clientX - metrics.rect.left - metrics.offsetX) / metrics.displayWidth;
    const relativeY = (clientY - metrics.rect.top - metrics.offsetY) / metrics.displayHeight;
    return {
      x: clamp(relativeX, 0, 1),
      y: clamp(relativeY, 0, 1),
    };
  };

  const detectEdges = (point) => {
    const threshold = 0.04;
    const edges = {};
    if (Math.abs(point.x - selection.x) <= threshold) edges.left = true;
    if (Math.abs(point.x - (selection.x + selection.width)) <= threshold) edges.right = true;
    if (Math.abs(point.y - selection.y) <= threshold) edges.top = true;
    if (Math.abs(point.y - (selection.y + selection.height)) <= threshold) edges.bottom = true;
    return Object.keys(edges).length ? edges : null;
  };

  const isPointInsideSelection = (point) =>
    point.x >= selection.x &&
    point.x <= selection.x + selection.width &&
    point.y >= selection.y &&
    point.y <= selection.y + selection.height;

  const clampSelection = (sel) => {
    const width = clamp(sel.width, MIN_SELECTION_SIZE, 1);
    const height = clamp(sel.height, MIN_SELECTION_SIZE, 1);
    const x = clamp(sel.x, 0, 1 - width);
    const y = clamp(sel.y, 0, 1 - height);
    return { x, y, width, height };
  };

  useEffect(() => {
    if (!isEditModalOpen || !loadedImage || !editCanvasRef.current) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      const metrics = getDisplayMetrics();
      if (!editCanvasRef.current || !metrics) return;

      const canvas = editCanvasRef.current;
      const canvasWidth = Math.round(metrics.rect.width);
      const canvasHeight = Math.round(metrics.rect.height);
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(
        loadedImage,
        metrics.offsetX,
        metrics.offsetY,
        metrics.displayWidth,
        metrics.displayHeight
      );
      const selX = metrics.offsetX + selection.x * metrics.displayWidth;
      const selY = metrics.offsetY + selection.y * metrics.displayHeight;
      const selWidth = Math.max(1, selection.width * metrics.displayWidth);
      const selHeight = Math.max(1, selection.height * metrics.displayHeight);
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.save();
      ctx.beginPath();
      ctx.rect(selX, selY, selWidth, selHeight);
      ctx.clip();
      ctx.drawImage(
        loadedImage,
        metrics.offsetX,
        metrics.offsetY,
        metrics.displayWidth,
        metrics.displayHeight
      );
      ctx.restore();
      ctx.strokeStyle = '#ff0099';
      ctx.lineWidth = 2;
      ctx.strokeRect(selX, selY, selWidth, selHeight);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [loadedImage, selection, isEditModalOpen, editorAspectRatio, getDisplayMetrics]);

  const stopDragging = () => {
    if (dragStateRef.current?.pointerId && editAreaRef.current) {
      editAreaRef.current.releasePointerCapture?.(dragStateRef.current.pointerId);
    }
    dragStateRef.current = null;
  };

  const handlePointerDown = (event) => {
    const metrics = getDisplayMetrics();
    if (!metrics) return;
    event.preventDefault();
    const point = pointToImageCoords(event.clientX, event.clientY, metrics);
    if (!point) return;
    const edges = detectEdges(point);
    const inside = isPointInsideSelection(point);
    const mode = edges ? 'resize' : inside ? 'move' : 'draw';
    dragStateRef.current = {
      pointerId: event.pointerId,
      startPointerX: point.x,
      startPointerY: point.y,
      startSelection: { ...selection },
      mode,
      edges,
      metrics,
    };
    editAreaRef.current.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragStateRef.current) return;
    const { mode, startPointerX, startPointerY, startSelection, edges } = dragStateRef.current;
    const metrics = dragStateRef.current.metrics || getDisplayMetrics();
    if (!metrics) return;
    const point = pointToImageCoords(event.clientX, event.clientY, metrics);
    if (!point) return;
    let newSelection = { ...startSelection };
    const deltaX = point.x - startPointerX;
    const deltaY = point.y - startPointerY;
    if (mode === 'move') {
      const newX = clamp(startSelection.x + deltaX, 0, 1 - startSelection.width);
      const newY = clamp(startSelection.y + deltaY, 0, 1 - startSelection.height);
      newSelection.x = newX;
      newSelection.y = newY;
    } else if (mode === 'resize' && edges) {
      if (edges.left) {
        const rawX = startSelection.x + deltaX;
        const clampedX = clamp(rawX, 0, startSelection.x + startSelection.width - MIN_SELECTION_SIZE);
        const diff = clampedX - startSelection.x;
        newSelection.x = clampedX;
        newSelection.width = startSelection.width - diff;
      }
      if (edges.right) {
        const rawWidth = startSelection.width + deltaX;
        newSelection.width = clamp(rawWidth, MIN_SELECTION_SIZE, 1 - startSelection.x);
      }
      if (edges.top) {
        const rawY = startSelection.y + deltaY;
        const clampedY = clamp(rawY, 0, startSelection.y + startSelection.height - MIN_SELECTION_SIZE);
        const diff = clampedY - startSelection.y;
        newSelection.y = clampedY;
        newSelection.height = startSelection.height - diff;
      }
      if (edges.bottom) {
        const rawHeight = startSelection.height + deltaY;
        newSelection.height = clamp(rawHeight, MIN_SELECTION_SIZE, 1 - startSelection.y);
      }
    } else if (mode === 'draw') {
      const startX = startPointerX;
      const startY = startPointerY;
      const newX = clamp(Math.min(startX, point.x), 0, 1 - MIN_SELECTION_SIZE);
      const newY = clamp(Math.min(startY, point.y), 0, 1 - MIN_SELECTION_SIZE);
      const newWidth = clamp(Math.max(Math.abs(point.x - startX), MIN_SELECTION_SIZE), MIN_SELECTION_SIZE, 1 - newX);
      const newHeight = clamp(Math.max(Math.abs(point.y - startY), MIN_SELECTION_SIZE), MIN_SELECTION_SIZE, 1 - newY);
      newSelection = { x: newX, y: newY, width: newWidth, height: newHeight };
    }
    setSelection(clampSelection(newSelection));
  };

  const handlePointerUp = () => {
    stopDragging();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setNewMediaFile(null);
      return;
    }

    const isSupportedType =
      selectedFile.type.startsWith('image/') || selectedFile.type.startsWith('video/');
    if (!isSupportedType) {
      setUploadError('Only image and video files are supported.');
      setNewMediaFile(null);
      e.target.value = '';
      return;
    }

    if (selectedFile.size > MAX_MEDIA_SIZE_BYTES) {
      setUploadError('File is too large. Maximum size is 50MB.');
      setNewMediaFile(null);
      e.target.value = '';
      return;
    }

    setUploadError('');
    setUploadSuccess('');
    setNewMediaFile(selectedFile);

    if (selectedFile.type.startsWith('image/')) {
      const previewObjectUrl = URL.createObjectURL(selectedFile);
      updatePreviewUrl(previewObjectUrl);
      setIsEditModalOpen(true);
      setSelection({ x: 0, y: 0, width: 1, height: 1 });
      loadImage(previewObjectUrl)
        .then((img) => setLoadedImage(img))
        .catch(() => setLoadedImage(null));
    } else {
      revokePreviewUrl();
      setPreviewUrl('');
      setIsEditModalOpen(false);
      setLoadedImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUploading) return;

    if (!newMediaName.trim()) {
      setUploadSuccess('');
      setUploadError('Please enter a media name.');
      return;
    }

    if (!newMediaFile) {
      setUploadSuccess('');
      setUploadError('Please choose a file to upload.');
      return;
    }

    setUploadError('');
    setUploadSuccess('');
    setIsUploading(true);
    try {
      await onUpload();
      setUploadSuccess('Chapter uploaded successfully.');
    } catch (err) {
      setUploadError(err?.message || 'Upload failed. Please try again.');
      setUploadSuccess('');
    } finally {
      setIsUploading(false);
    }
  };

  const applyImageCrop = async () => {
    if (!loadedImage || !newMediaFile) return;
    setIsCropBusy(true);
    try {
      const blob = await cropImageToBlob(loadedImage, clampSelection(selection), newMediaFile.type);
      const croppedFile = new File([blob], newMediaFile.name, { type: blob.type });
      setNewMediaFile(croppedFile);
      const croppedUrl = URL.createObjectURL(croppedFile);
      updatePreviewUrl(croppedUrl);
      await loadImage(croppedUrl).then((img) => setLoadedImage(img)).catch(() => setLoadedImage(null));
      setIsEditModalOpen(false);
      setUploadError('');
      setUploadSuccess('');
    } catch (err) {
      setUploadError(err?.message || 'Unable to apply the crop; try again.');
    } finally {
      setIsCropBusy(false);
    }
  };

  const handleEditClose = () => {
    stopDragging();
    setIsEditModalOpen(false);
  };

  const createMessageCardFile = async () => {
    if (!messageText.trim()) {
      setUploadError('Please enter a message for the card.');
      return;
    }

    setIsMessageBusy(true);
    setUploadError('');
    setUploadSuccess('');

    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = messageBackground;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const gradient = ctx.createRadialGradient(540, 520, 40, 540, 520, 980);
      gradient.addColorStop(0, 'rgba(255,255,255,0.62)');
      gradient.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = 'rgba(255,255,255,0.68)';
      ctx.lineWidth = 10;
      ctx.strokeRect(58, 58, canvas.width - 116, canvas.height - 116);
      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 2;
      ctx.strokeRect(84, 84, canvas.width - 168, canvas.height - 168);

      const fontStyle = messageItalic ? 'italic ' : '';
      const fontWeight = messageBold ? '800' : '600';
      ctx.font = `${fontStyle}${fontWeight} ${messageFontSize}px Georgia, serif`;
      ctx.fillStyle = messageTextColor;
      ctx.textAlign = messageAlign;
      ctx.textBaseline = 'middle';

      const maxTextWidth = canvas.width - 220;
      const lines = wrapCanvasText(ctx, messageText, maxTextWidth);
      const lineHeight = messageFontSize * 1.32;
      const totalTextHeight = lines.length * lineHeight;
      let y = (canvas.height - totalTextHeight) / 2 + lineHeight / 2;
      const xByAlign = {
        left: 130,
        center: canvas.width / 2,
        right: canvas.width - 130,
      };
      const x = xByAlign[messageAlign] || canvas.width / 2;

      lines.forEach((line) => {
        ctx.fillText(line, x, y);
        y += lineHeight;
      });

      ctx.font = '700 26px Arial, sans-serif';
      ctx.fillStyle = messageTextColor;
      ctx.globalAlpha = 0.55;
      ctx.textAlign = 'center';
      ctx.fillText('A personal note', canvas.width / 2, canvas.height - 140);
      ctx.globalAlpha = 1;

      const blob = await canvasToBlob(canvas, 'image/png');
      const safeName = (newMediaName || 'personal-message-card')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'personal-message-card';
      const file = new File([blob], `${safeName}.png`, { type: 'image/png' });
      const previewObjectUrl = URL.createObjectURL(file);

      setNewMediaName(newMediaName.trim() || 'Personal message');
      setNewMediaFile(file);
      updatePreviewUrl(previewObjectUrl);
      await loadImage(previewObjectUrl).then((img) => setLoadedImage(img)).catch(() => setLoadedImage(null));
      setIsMessageModalOpen(false);
      setIsEditModalOpen(false);
      setUploadSuccess('Message card created. Review visibility, then upload it as a chapter.');
    } catch (err) {
      setUploadError(err?.message || 'Unable to create message card.');
    } finally {
      setIsMessageBusy(false);
    }
  };

  const isUploadDisabled = isUploading;

  const handleToggleMediaAccess = async (file) => {
    if (!onToggleMediaAccess) return;
    if (deleteUpdateById[file.id]) return;

    setAccessUpdateById((prev) => ({ ...prev, [file.id]: true }));
    setAccessErrorById((prev) => ({ ...prev, [file.id]: '' }));

    try {
      await onToggleMediaAccess(file);
    } catch (err) {
      setAccessErrorById((prev) => ({
        ...prev,
        [file.id]: err?.message || 'Failed to update media visibility.',
      }));
    } finally {
      setAccessUpdateById((prev) => ({ ...prev, [file.id]: false }));
    }
  };

  const handleDeleteMedia = async (file) => {
    if (!onDeleteMedia) return;
    if (deleteUpdateById[file.id]) return;

    const shouldDelete = window.confirm(`Delete "${file.name}" permanently?`);
    if (!shouldDelete) return;

    setDeleteUpdateById((prev) => ({ ...prev, [file.id]: true }));
    setDeleteErrorById((prev) => ({ ...prev, [file.id]: '' }));
    setAccessErrorById((prev) => ({ ...prev, [file.id]: '' }));

    try {
      await onDeleteMedia(file);
    } catch (err) {
      setDeleteErrorById((prev) => ({
        ...prev,
        [file.id]: err?.message || 'Failed to delete media.',
      }));
    } finally {
      setDeleteUpdateById((prev) => ({ ...prev, [file.id]: false }));
    }
  };

  return (
    <section className="space-y-8">
      <div className="flex items-end gap-3">
        <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center font-black shadow-lg">
          <Upload size={18} />
        </div>
        <div>
          <h2 className="text-3xl font-black tracking-tight text-gray-900">Extra Media Chapters</h2>
          <p className="text-gray-500 text-sm font-medium">
            Optional photos or videos to share with everyone, selected groups, or individual guests.
          </p>
        </div>
      </div>

      <div className="rounded-[32px] border border-[#f3dce9] bg-[#fff8fc] p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-[#ff0099] shadow-sm">
              <BookOpen size={20} />
            </span>
            <div>
              <h3 className="text-base font-black text-gray-950">Think of each upload as an extra story chapter.</h3>
              <p className="mt-1 text-sm font-medium leading-6 text-gray-500">
                Use this only when you want to share extra photos or videos beyond the main invitation,
                such as family memories, venue clips, travel notes, or a private message for selected guests.
              </p>
            </div>
          </div>
          <div className="rounded-2xl bg-white px-4 py-3 text-xs font-bold leading-5 text-gray-500 shadow-sm md:max-w-xs">
            Public chapters are visible to everyone. Private chapters can be shared with selected guests or groups.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Upload Card */}
        <div className="bg-white p-6 rounded-[35px] shadow-xl border-2 border-dashed border-pink-100 flex flex-col items-center justify-center text-center group hover:border-[#ff0099] transition-all relative overflow-hidden">
          <form onSubmit={handleSubmit} className="w-full space-y-3">
            <div className="w-12 h-12 bg-pink-50 rounded-2xl flex items-center justify-center text-[#ff0099] mx-auto group-hover:scale-110 transition-transform">
              <Upload size={24} />
            </div>
            <div>
              <p className="text-sm font-black text-gray-900">Upload a chapter</p>
              <p className="mt-1 text-[10px] font-semibold leading-4 text-gray-400">
                Add a clear title, then choose an image/video or create a message card.
              </p>
            </div>
            <label className="block text-left text-[10px] font-black uppercase tracking-widest text-gray-500">
              Chapter name <span className="text-[#ff0099]">*</span>
            </label>
            <input 
              placeholder="e.g. Family welcome video" 
              className="w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm font-semibold outline-none focus:border-[#ff0099] focus:bg-white" 
              value={newMediaName} 
              maxLength={80}
              onChange={e => {
                setUploadError('');
                setUploadSuccess('');
                setNewMediaName(e.target.value);
              }}
              aria-label="Media name"
            />
            <input 
              type="file" 
              className="hidden" 
              id={mediaUploadInputId}
              accept="image/*,video/*"
              onChange={handleFileChange}
            />
            <label className="block text-left text-[10px] font-black uppercase tracking-widest text-gray-500">
              Media file <span className="text-[#ff0099]">*</span>
            </label>
            <label htmlFor={mediaUploadInputId} className="block rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs font-black text-gray-500 uppercase cursor-pointer hover:border-[#ff0099] hover:text-[#ff0099]">
              {newMediaFile ? newMediaFile.name : "Choose image or video"}
            </label>
            <button
              type="button"
              onClick={() => {
                setUploadError('');
                setUploadSuccess('');
                setIsMessageModalOpen(true);
              }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-pink-100 bg-[#fff8fc] px-4 py-3 text-xs font-black uppercase tracking-wide text-[#b12d75] hover:border-[#ff0099] hover:text-[#ff0099]"
            >
              <MessageSquare size={14} /> Create message card
            </button>
            {newMediaFile?.type?.startsWith('image/') && (
              <div className="space-y-1 pt-3 text-left w-full">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(true)}
                  className="w-full rounded-[20px] border border-gray-200 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-600 hover:border-[#ff0099] hover:text-[#ff0099] transition"
                >
                  Open crop editor
                </button>
                {previewUrl && (
                  <p className="text-[9px] text-gray-500">
                    Draw or drag a new selection to shape the crop; the Apply button saves exactly what you see.
                  </p>
                )}
              </div>
            )}
            <label className="flex items-center justify-center gap-2 cursor-pointer mt-2 rounded-2xl bg-gray-50 px-4 py-3">
              <input 
                type="checkbox" 
                checked={isMediaPublic} 
                onChange={e => setIsMediaPublic(e.target.checked)} 
                className="accent-[#ff0099]" 
              />
              <span className="text-[10px] font-bold text-gray-500">
                Share with everyone by default
              </span>
            </label>
            <p className="flex items-start gap-2 text-left text-[10px] font-semibold leading-4 text-gray-400">
              <Info size={13} className="mt-0.5 shrink-0" />
              Turn this off if the chapter should be private, then grant access by group or guest after upload.
            </p>
            {uploadError && (
              <p className="text-[10px] font-bold text-red-500">{uploadError}</p>
            )}
            {uploadSuccess && (
              <p className="text-[10px] font-bold text-green-600">{uploadSuccess}</p>
            )}
            <button
              type="submit"
              disabled={isUploadDisabled}
              className="w-full py-2 bg-gray-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-[#ff0099] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Upload Chapter'}
            </button>
          </form>
        </div>

        {isMessageModalOpen && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4 py-6">
            <div
              className="w-full max-w-4xl space-y-5 rounded-[30px] bg-white p-6 shadow-2xl overflow-y-auto"
              style={{ width: 'min(95vw, 980px)', maxHeight: '90vh' }}
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-sm font-black text-gray-900">Create a personal message card</p>
                  <p className="text-[11px] font-medium leading-5 text-gray-500">
                    This will be saved as an image chapter, so you can make it public or private and share it with selected guests.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMessageModalOpen(false)}
                  className="text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-[#ff0099]"
                >
                  Close
                </button>
              </div>

              <div className="grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
                <div className="space-y-4 rounded-[24px] border border-gray-100 bg-gray-50 p-4">
                  <label className="block text-left text-[10px] font-black uppercase tracking-widest text-gray-500">
                    Message text
                  </label>
                  <textarea
                    value={messageText}
                    onChange={(event) => setMessageText(event.target.value)}
                    placeholder="Write a personal note for your guests..."
                    className="min-h-[180px] w-full rounded-2xl border border-gray-100 bg-white px-4 py-3 text-sm font-semibold leading-6 outline-none focus:border-[#ff0099]"
                    maxLength={500}
                  />

                  <div>
                    <p className="mb-2 text-left text-[10px] font-black uppercase tracking-widest text-gray-500">
                      Background
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {messageCardBackgrounds.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setMessageBackground(color)}
                          className={`h-10 rounded-xl border-2 ${messageBackground === color ? 'border-[#ff0099]' : 'border-white'}`}
                          style={{ background: color }}
                          aria-label={`Use background ${color}`}
                        />
                      ))}
                    </div>
                  </div>

                  <label className="block text-left text-[10px] font-black uppercase tracking-widest text-gray-500">
                    Text color
                    <input
                      type="color"
                      value={messageTextColor}
                      onChange={(event) => setMessageTextColor(event.target.value)}
                      className="mt-2 h-11 w-full rounded-xl border border-gray-100 bg-white"
                    />
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-left text-[10px] font-black uppercase tracking-widest text-gray-500">
                      Font size
                      <input
                        type="range"
                        min="34"
                        max="86"
                        value={messageFontSize}
                        onChange={(event) => setMessageFontSize(Number(event.target.value))}
                        className="mt-3 w-full accent-[#ff0099]"
                      />
                    </label>
                    <label className="block text-left text-[10px] font-black uppercase tracking-widest text-gray-500">
                      Alignment
                      <select
                        value={messageAlign}
                        onChange={(event) => setMessageAlign(event.target.value)}
                        className="mt-2 w-full rounded-xl border border-gray-100 bg-white px-3 py-3 text-xs font-bold outline-none focus:border-[#ff0099]"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </label>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMessageBold((current) => !current)}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-3 text-xs font-black ${messageBold ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}
                    >
                      <Bold size={14} /> Bold
                    </button>
                    <button
                      type="button"
                      onClick={() => setMessageItalic((current) => !current)}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-3 text-xs font-black ${messageItalic ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}
                    >
                      <Italic size={14} /> Italic
                    </button>
                  </div>
                </div>

                <div className="rounded-[28px] border border-gray-100 bg-gray-100 p-4">
                  <div
                    className="mx-auto flex aspect-[9/16] max-h-[620px] w-full max-w-[360px] flex-col items-center justify-center overflow-hidden rounded-[26px] p-10 text-center shadow-xl"
                    style={{ background: messageBackground, color: messageTextColor }}
                  >
                    <p
                      className="whitespace-pre-wrap break-words font-serif leading-snug"
                      style={{
                        textAlign: messageAlign,
                        fontSize: `${Math.max(18, messageFontSize * 0.38)}px`,
                        fontWeight: messageBold ? 800 : 600,
                        fontStyle: messageItalic ? 'italic' : 'normal',
                      }}
                    >
                      {messageText || 'Your personal message will appear here.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsMessageModalOpen(false)}
                  className="rounded-[20px] border border-gray-200 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:border-[#ff0099] hover:text-[#ff0099] transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isMessageBusy}
                  onClick={createMessageCardFile}
                  className="rounded-[20px] bg-[#ff0099] px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isMessageBusy ? 'Creating...' : 'Use this message card'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isEditModalOpen && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4 py-6">
            <div
              className="w-full max-w-3xl space-y-4 rounded-[30px] bg-white p-6 shadow-2xl overflow-y-auto"
              style={{ width: 'min(95vw, 960px)', maxHeight: '90vh' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-black text-gray-900">Fine-tune image</p>
                  <p className="text-[10px] text-gray-500">
                    Draw a new selection or drag the existing one to cover the area you want to keep.
                    Move inside the box to pan it, or pull edges to resize.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleEditClose}
                  className="text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-[#ff0099]"
                >
                  Close
                </button>
              </div>
              <div
                ref={editAreaRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                className="relative overflow-hidden rounded-[25px] border border-gray-200 bg-black/5 cursor-crosshair w-full"
                style={{ aspectRatio: editorAspectRatio, maxHeight: 'min(70vh, 640px)' }}
              >
                <canvas
                  ref={editCanvasRef}
                  className="absolute inset-0 h-full w-full"
                  aria-label="Image preview for cropping"
                />
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleEditClose}
                  className="rounded-[20px] border border-gray-200 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:border-[#ff0099] hover:text-[#ff0099] transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isCropBusy}
                  onClick={applyImageCrop}
                  className="rounded-[20px] bg-[#ff0099] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCropBusy ? 'Applying...' : 'Apply Crop'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Media Display Cards */}
        {isMediaLoading && (
          <div className="bg-white p-6 rounded-[35px] shadow-lg border border-gray-100 flex items-center justify-center text-center">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Loading media...</p>
          </div>
        )}

        {!isMediaLoading && mediaError && (
          <div className="bg-white p-6 rounded-[35px] shadow-lg border border-red-100 flex items-center justify-center text-center">
            <p className="text-xs font-bold text-red-500">{mediaError}</p>
          </div>
        )}

        {!isMediaLoading && !mediaError && mediaFiles.length === 0 && (
          <div className="bg-white p-6 rounded-[35px] shadow-lg border border-gray-100 flex items-center justify-center text-center">
            <p className="text-xs font-bold text-gray-500">No media chapters yet. Upload your first one.</p>
          </div>
        )}

        {!isMediaLoading && !mediaError && mediaFiles.map((file) => (
          <MediaCard 
            key={file.id} 
            file={file} 
            isAccessUpdating={Boolean(accessUpdateById[file.id])}
            accessError={accessErrorById[file.id]}
            isDeleting={Boolean(deleteUpdateById[file.id])}
            deleteError={deleteErrorById[file.id]}
            onToggleAccess={() => handleToggleMediaAccess(file)}
            onDelete={() => handleDeleteMedia(file)}
            onAddTag={() => onAddTagAccess(file)} 
          />
        ))}
      </div>
    </section>
  );
};

const MediaCard = ({ file, onAddTag, onToggleAccess, isAccessUpdating, accessError, onDelete, isDeleting, deleteError }) => (
  <div className="bg-white p-4 pb-10 rounded-[35px] shadow-lg border border-white relative group overflow-hidden flex flex-col">
    <div className="aspect-video bg-gray-100 rounded-[25px] mb-3 flex items-center justify-center overflow-hidden border border-gray-50">
      {file.file_type?.includes('video') ? (
        <video src={file.file_path} className="w-full h-full object-cover" muted />
      ) : (
        <img 
          src={file.file_path} 
          alt={file.name} 
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=No+Preview'; }}
        />
      )}
    </div>
    <div className="px-1 mt-auto">
      <p className="font-bold text-sm truncate text-gray-700">{file.name}</p>
      <div className="flex items-center gap-2 mt-1">
        {file.is_public || file.media_type === "user_public" ? (
          <span className="text-[9px] font-black text-green-500 uppercase flex items-center gap-1"><Globe size={10}/> Everyone</span>
        ) : (
          <span className="text-[9px] font-black text-purple-500 uppercase flex items-center gap-1"><Lock size={10}/> Private Access</span>
        )}
      </div>
      <button
        type="button"
        onClick={onToggleAccess}
        disabled={isAccessUpdating || isDeleting}
        className="mt-2 text-[10px] font-black uppercase tracking-wide text-gray-500 hover:text-[#ff0099] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isAccessUpdating
          ? 'Updating...'
          : file.is_public || file.media_type === "user_public"
            ? 'Make Private'
            : 'Make Public'}
      </button>
      {accessError && <p className="text-[10px] font-bold text-red-500 mt-1">{accessError}</p>}
      {deleteError && <p className="text-[10px] font-bold text-red-500 mt-1">{deleteError}</p>}
    </div>
    
    <div className="mt-4 pt-4 border-t border-gray-50">
      <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Tag Access</p>
      <div className="flex flex-wrap gap-1">
        {(file.permitted_tags || []).map((tagItem, index) => {
          const tagId = tagItem?.tag?.id ?? `tag-${file.id}-${index}`;
          const tagName = tagItem?.tag?.name;
          if (!tagName) return null;

          return (
            <span key={tagId} className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded-md text-[9px] font-bold">
              #{tagName}
            </span>
          );
        })}
        <button onClick={onAddTag} className="p-1 bg-gray-50 text-gray-400 hover:text-[#ff0099] rounded-md">
          <Plus size={12} />
        </button>
      </div>
    </div>

    <button
      type="button"
      onClick={onDelete}
      disabled={isDeleting || isAccessUpdating}
      aria-label="Delete media"
      title={isDeleting ? 'Deleting...' : 'Delete media'}
      className="absolute bottom-3 right-3 p-1.5 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Trash2 size={12} />
    </button>
  </div>
);
